/**
 * Portal Entry Point for Forms Domain
 *
 * Registers all pages and custom CRUD components with the portal system.
 *
 * Custom Components (SSOT):
 * - Forms::SchemaBuilder - Block for drag-and-drop form schema editing
 * - Forms::WorkflowConfig - Block for workflow/auto-complete configuration
 * - Forms::ReportingConfig - Block for scoring, issues, and pass/fail thresholds
 * - Forms::FormSubmissionReportBlock - Block rendering submission report in the FormSubmission viewer
 * - Forms::FormSubmissionReviewBlock - Block for the post-submission multi-stage review workflow
 * - Forms::FormSubmissionPdfExportBlock - Block for dispatching a Gotenberg PDF export of a submission
 * - Forms::FormSubmissionFilesBlock - Block for listing/routing submission attachments to linked profiles
 * - Forms::AssignmentsTab - Tab for managing direct and communication-driven assignments
 * - Forms::AnalyticsTab - Tab for submission metrics, scores, and timelines
 * - Forms::ImportExportTab - Tab for global form definition import/export
 */

import {
    registerBlockComponent,
    registerTabComponent,
} from "@ja/crud/services/componentRegistry";
import { registerPage } from "@ja/portal/pageRegistry";

// ============================================================================
// Pages
// ============================================================================

registerPage(
    "forms",
    "Base/FormAssignment",
    () => import("./Pages/FormAssignment.vue"),
);

// ============================================================================
// Custom CRUD Components (SSOT Registration)
// ============================================================================

/**
 * SchemaBuilder - Block for editing form schema (sections, fields, rules).
 * Used in the editor "schema" tab.
 */
registerBlockComponent(
    "Forms::SchemaBuilder",
    () => import("./Components/CrudBlocks/SchemaBuilder.vue"),
    "forms",
);

/**
 * WorkflowConfig - Block for configuring workflow behavior.
 * Controls auto-complete task, delivery behavior, and assignment rules.
 */
registerBlockComponent(
    "Forms::WorkflowConfig",
    () => import("./Components/CrudBlocks/WorkflowConfig.vue"),
    "forms",
);

/**
 * ReportingConfig - Block for configuring reporting.
 * Controls score calculation, issue extraction, pass/fail thresholds.
 */
registerBlockComponent(
    "Forms::ReportingConfig",
    () => import("./Components/CrudBlocks/ReportingConfig.vue"),
    "forms",
);

/**
 * PartyTaskFormsBlock - Block for managing before/after form attachments
 * on PartyTask editor. Registered from Forms domain since it owns the pivot.
 */
registerBlockComponent(
    "Forms::PartyTaskFormsBlock",
    () => import("./Components/CrudBlocks/PartyTaskFormsBlock.vue"),
    "forms",
);

/**
 * FormSubmissionReportBlock - Block for the FormSubmission viewer.
 * Renders the full submission report (score, issues, sections, answers)
 * using the shared FormReportViewer component.
 */
registerBlockComponent(
    "Forms::FormSubmissionReportBlock",
    () => import("./Components/CrudBlocks/FormSubmissionReportBlock.vue"),
    "forms",
);

/**
 * FormSubmissionReviewBlock - Block for the FormSubmission viewer.
 * Renders the post-submission multi-stage review workflow with
 * Advance / Return actions gated on the current stage's actor_roles.
 */
registerBlockComponent(
    "Forms::FormSubmissionReviewBlock",
    () => import("./Components/CrudBlocks/FormSubmissionReviewBlock.vue"),
    "forms",
);

/**
 * FormSubmissionPdfExportBlock - Block for the FormSubmission viewer.
 * Dispatches an async Gotenberg PDF export and surfaces the download link.
 */
registerBlockComponent(
    "Forms::FormSubmissionPdfExportBlock",
    () => import("./Components/CrudBlocks/FormSubmissionPdfExportBlock.vue"),
    "forms",
);

/**
 * FormSubmissionFilesBlock - Block for the FormSubmission viewer.
 * Lists submission attachments and lets admins manually route each to a
 * linked PartyProfile or User profile's `documents` collection. Works
 * alongside schema-driven automated routing.
 */
registerBlockComponent(
    "Forms::FormSubmissionFilesBlock",
    () => import("./Components/CrudBlocks/FormSubmissionFilesBlock.vue"),
    "forms",
);

/**
 * AssignmentsTab - Tab for managing form assignments.
 * Supports direct assignment and communication-driven distribution.
 */
registerTabComponent(
    "Forms::AssignmentsTab",
    () => import("./Components/CrudTabs/AssignmentsTab.vue"),
    "forms",
);

/**
 * AnalyticsTab - Tab for form analytics and charts.
 * Shows submission metrics, scores, issues, and timelines.
 */
registerTabComponent(
    "Forms::AnalyticsTab",
    () => import("./Components/CrudTabs/AnalyticsTab.vue"),
    "forms",
);

/**
 * ImportExportTab - Tab for global form import/export.
 * Bundles details, schema, workflow, and reporting into a single JSON file.
 */
registerTabComponent(
    "Forms::ImportExportTab",
    () => import("./Components/CrudTabs/ImportExportTab.vue"),
    "forms",
);

/**
 * SidebarExposureTab - "Visibilité" tab. Kind-level sidebar exposure +
 * per-role access matrix. Edits apply to all forms of the kind.
 */
registerTabComponent(
    "Forms::SidebarExposureTab",
    () => import("./Components/CrudTabs/SidebarExposureTab.vue"),
    "forms",
);

/**
 * KindScopedSubmissionCreator - Creator block for kind-scoped sidebar entries.
 * Lists published forms of the kind and starts a self-assignment fill flow.
 */
registerBlockComponent(
    "Forms::KindScopedSubmissionCreator",
    () => import("./Components/CrudBlocks/KindScopedSubmissionCreator.vue"),
    "forms",
);
